import { Row, Col, Form, DatePicker, Button, message } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";

import { useSelector } from "react-redux";
import APICaller from "../../utils/APICaller";
import CustomLoader from "../../components/CustomLoader";

export default function ExpenseCalendarControl() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [blockedDate, setBlockedDate] = useState(null);

  const storeRole = useSelector((state) => state.auth.role);
  const storeToken = useSelector((state) => state.auth.accessToken);
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn
  );
  const storeLIUId = useSelector((state) => state.auth.lIUId);

  const expCal = () => {
    setShowLoader(true);
    const endpoint = "/expCal";
    const method = "POST";
    const payload = {
      role: storeRole,
      lIUId: storeLIUId,
    };

    APICaller(
      storeRole,
      storeToken,
      endpoint,
      method,
      payload,
      storeAccessTokenExpiresOn
    )
      .then((response) => {
        if (response?.expCalRes) {
          setBlockedDate(response.expCalRes);
        }
      })
      .catch((error) => {})
      .finally(() => {
        setShowLoader(false);
      });
  };

  const handleBlock = () => {
    if (!selectedDate) {
      message.warning("Please select a date.");
      return;
    }

    setShowLoader(true);
    const endpoint = "/manage-expense-calendar";
    const method = "POST";
    const payload = {
      blockedOn: selectedDate ? selectedDate.format("YYYY-MM-DD") : null,
      historySource: "backend",
      historyAction: "block",
      historyUserID: storeLIUId,
      status: process.env.REACT_APP_BLOCK_STATUS,
    };
    APICaller(
      storeRole,
      storeToken,
      endpoint,
      method,
      payload,
      storeAccessTokenExpiresOn
    )
      .then((response) => {
        if (response) {
          message.success("Calendar date blocked successfully.");
          setBlockedDate({
            ...payload,
            _id: response?._id || blockedDate?._id,
          });
          setSelectedDate(null);
        }
      })
      .catch(() => {
        message.error("Failed to block calendar date.");
      })
      .finally(() => setShowLoader(false));
  };

  const handleUnblock = () => {
    if (!blockedDate) {
      message.warning("No blocked date to unblock.");
      return;
    }
    setShowLoader(true);
    const endpoint = "/manage-expense-calendar";
    const method = "POST";
    const payload = {
      blockedOn: blockedDate?.blockedOn,
      historySource: "backend",
      historyAction: "unblock",
      historyUserID: storeLIUId,
      status: process.env.REACT_APP_UNBLOCK_STATUS,
    };
    APICaller(
      storeRole,
      storeToken,
      endpoint,
      method, // Or PUT/DELETE based on API support
      payload,
      storeAccessTokenExpiresOn
    )
      .then((response) => {
        if (response === process.env.REACT_APP_UNBLOCK_STATUS) {
          message.success("Calendar date unblocked successfully.");
          setBlockedDate(null);
          setSelectedDate(null);
        }
      })
      .catch(() => {
        message.error("Failed to unblock calendar date.");
      })
      .finally(() => setShowLoader(false));
  };

  useEffect(() => {
    if (blockedDate) {
      if (blockedDate.status === process.env.REACT_APP_BLOCK_STATUS) {
        setSelectedDate(dayjs(blockedDate.blockedOn));
      } else {
        setSelectedDate(null);
      }
    }
  }, [blockedDate]);

  useEffect(() => {
    expCal();
  }, []);

  return (
    <div className="container">
      <CustomLoader showLoader={showLoader} />
      {showLoader ? null : (
        <div className="card border-2 rounded-lg p-0">
          <div className="card-header p-6">
            <h3 className="font-weight-bolder text-dark font-size-lg m-0">
              Block / Unblock Calendar Date
            </h3>
          </div>
          <div className="card-body p-3">
            <Form layout="vertical">
              <Row gutter={[16, 16]} align="bottom">
                <Col sm={8}>
                  <Form.Item label="" className="m-0">
                    <DatePicker
                      value={selectedDate}
                      onChange={(date) => {
                        setSelectedDate(date); // set the dayjs object directly, or null if cleared
                      }}
                      disabled={
                        blockedDate?.status ===
                        process.env.REACT_APP_BLOCK_STATUS
                      }
                      allowClear={true} // optional: allows clearing the date
                      placeholder="Select date"
                      className="form-control h-45px rounded-md d-inline-flex"
                    />
                  </Form.Item>
                </Col>

                {blockedDate?.status === process.env.REACT_APP_BLOCK_STATUS ? (
                  <Col sm={"auto"}>
                    <Form.Item className="m-0">
                      <Button
                        danger
                        loading={showLoader}
                        onClick={handleUnblock}
                        className="btn font-size-h5 font-weight-bold px-10 py-3 redbutton rounded-lg text-white"
                      >
                        Unblock
                      </Button>
                    </Form.Item>
                  </Col>
                ) : (
                  <Col sm={"auto"}>
                    <Form.Item className="m-0">
                      <Button
                        type="primary"
                        loading={showLoader}
                        onClick={handleBlock}
                        className="btn font-size-h5 font-weight-bold px-10 py-3 redbutton rounded-lg text-white"
                      >
                        Block
                      </Button>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
}
